import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { FeatureGate } from '../components/FeatureGate';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertCircle, 
  User, 
  Calendar,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Eye,
  Check,
  X as XIcon,
  Users,
  ChevronDown,
  CalendarDays
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { format, parseISO } from 'date-fns';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-500', icon: Clock },
  started: { label: 'In Progress', color: 'bg-blue-500', icon: Clock },
  completed: { label: 'Completed', color: 'bg-yellow-500', icon: AlertCircle },
  submitted: { label: 'Awaiting Approval', color: 'bg-orange-500', icon: AlertCircle },
  approved: { label: 'Approved', color: 'bg-green-500', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-500', icon: XCircle }
};

const PAY_CYCLE_LABELS = {
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  monthly: 'Monthly'
};

const DAY_NAMES = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const Timesheets = () => {
  const { api, user } = useAuth();
  const { hasFeature } = useTier();
  const [activeTab, setActiveTab] = useState('all');
  const [groupedData, setGroupedData] = useState({ pay_cycle_type: 'weekly', pay_cycle_day: 1, groups: [] });
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [operatorFilter, setOperatorFilter] = useState('all');
  
  // Group drawer state
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupDrawerOpen, setGroupDrawerOpen] = useState(false);
  const [selectedTimesheets, setSelectedTimesheets] = useState([]);
  const [expandedOperator, setExpandedOperator] = useState(null);
  
  // Individual timesheet view state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Stats
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    totalHours: 0
  });

  const fetchTimesheets = useCallback(async () => {
    if (!hasFeature('timesheets')) return;
    setLoading(true);
    try {
      const statusParam = activeTab === 'all' ? '' : activeTab === 'pending' ? 'submitted' : activeTab;
      const response = await api.get(`/timesheets/admin/grouped${statusParam ? `?status=${statusParam}` : ''}`);
      setGroupedData(response.data);
      
      // Calculate stats from all groups
      let pending = 0, approved = 0, rejected = 0, totalHours = 0;
      response.data.groups.forEach(group => {
        pending += group.pending_count || 0;
        approved += group.approved_count || 0;
        rejected += group.rejected_count || 0;
        totalHours += group.total_hours || 0;
      });
      
      setStats({ pending, approved, rejected, totalHours });
    } catch (error) {
      toast.error('Failed to load timesheets');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [api, activeTab, hasFeature]);

  const fetchOperators = useCallback(async () => {
    try {
      const response = await api.get('/operators');
      setOperators(response.data);
    } catch (error) {
      console.error('Failed to load operators');
    }
  }, [api]);

  useEffect(() => {
    fetchTimesheets();
    fetchOperators();
  }, [fetchTimesheets, fetchOperators]);

  const handleApprove = async (timesheetId) => {
    try {
      await api.post(`/timesheets/${timesheetId}/approval`, { action: 'approve' });
      toast.success('Timesheet approved');
      fetchTimesheets();
      // Update selected group if open
      if (selectedGroup) {
        const updatedTimesheets = selectedGroup.timesheets.map(ts => 
          ts.id === timesheetId ? { ...ts, status: 'approved' } : ts
        );
        setSelectedGroup({ ...selectedGroup, timesheets: updatedTimesheets });
      }
      setSelectedTimesheets(prev => prev.filter(id => id !== timesheetId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve timesheet');
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await api.post(`/timesheets/${selectedTimesheet.id}/approval`, { 
        action: 'reject',
        reason: rejectionReason 
      });
      toast.success('Timesheet rejected');
      setRejectDialogOpen(false);
      setRejectionReason('');
      setSelectedTimesheet(null);
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject timesheet');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedTimesheets.length === 0) {
      toast.error('No timesheets selected');
      return;
    }
    try {
      const response = await api.post('/timesheets/admin/bulk-approve', selectedTimesheets);
      toast.success(`${response.data.approved_count} timesheets approved`);
      setSelectedTimesheets([]);
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve timesheets');
    }
  };

  const toggleSelectTimesheet = (id) => {
    setSelectedTimesheets(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAllInGroup = () => {
    if (!selectedGroup) return;
    const pendingIds = selectedGroup.timesheets.filter(t => t.status === 'submitted').map(t => t.id);
    if (selectedTimesheets.length === pendingIds.length) {
      setSelectedTimesheets([]);
    } else {
      setSelectedTimesheets(pendingIds);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    // Handle HH:MM format (from mobile timesheets)
    if (timeString.match(/^\d{2}:\d{2}$/)) {
      return timeString;
    }
    // Handle ISO format
    try {
      return format(parseISO(timeString), 'HH:mm');
    } catch {
      return timeString;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(parseISO(dateString), 'dd MMM yyyy');
    } catch {
      return dateString;
    }
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(parseISO(dateString), 'dd MMM');
    } catch {
      return dateString;
    }
  };

  const formatPeriodLabel = (group) => {
    const start = formatDateShort(group.period_start);
    const end = formatDateShort(group.period_end);
    return `${start} - ${end}`;
  };

  const getPayCycleDescription = () => {
    const { pay_cycle_type, pay_cycle_day } = groupedData;
    if (pay_cycle_type === 'monthly') {
      return `${PAY_CYCLE_LABELS[pay_cycle_type]} (${pay_cycle_day}${getOrdinalSuffix(pay_cycle_day)} of month)`;
    }
    return `${PAY_CYCLE_LABELS[pay_cycle_type]} (${DAY_NAMES[pay_cycle_day]})`;
  };

  const getOrdinalSuffix = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  const openGroupDrawer = (group) => {
    setSelectedGroup(group);
    setSelectedTimesheets([]);
    setExpandedOperator(null);
    setGroupDrawerOpen(true);
  };

  // Group timesheets by operator within a pay cycle
  const getOperatorGroups = (timesheets) => {
    const groups = {};
    timesheets.forEach(ts => {
      const opId = ts.operator_id || 'unknown';
      if (!groups[opId]) {
        groups[opId] = {
          operator_id: opId,
          operator_name: ts.operator_name || 'Unknown',
          timesheets: [],
          total_hours: 0,
          pending_count: 0,
          approved_count: 0,
          rejected_count: 0
        };
      }
      groups[opId].timesheets.push(ts);
      groups[opId].total_hours += ts.total_hours || 0;
      if (ts.status === 'submitted') groups[opId].pending_count++;
      else if (ts.status === 'approved') groups[opId].approved_count++;
      else if (ts.status === 'rejected') groups[opId].rejected_count++;
    });
    return Object.values(groups).sort((a, b) => a.operator_name.localeCompare(b.operator_name));
  };

  // Filter groups by search/operator
  const filteredGroups = groupedData.groups.filter(group => {
    if (!searchTerm && operatorFilter === 'all') return true;
    
    // Check if any timesheet in group matches filters
    return group.timesheets.some(ts => {
      const matchesSearch = !searchTerm || 
        ts.operator_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ts.job_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ts.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesOperator = operatorFilter === 'all' || ts.operator_id === operatorFilter;
      return matchesSearch && matchesOperator;
    });
  });

  // Filter timesheets within selected group
  const filteredGroupTimesheets = selectedGroup?.timesheets.filter(ts => {
    const matchesSearch = !searchTerm || 
      ts.operator_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ts.job_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ts.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesOperator = operatorFilter === 'all' || ts.operator_id === operatorFilter;
    return matchesSearch && matchesOperator;
  }) || [];

  return (
    <FeatureGate 
      feature="timesheets"
      title="Timesheets"
      description="Review and approve operator timesheets submitted from the mobile app. Track hours worked, manage approvals, and maintain accurate payroll records."
    >
      <div className="space-y-6" data-testid="timesheets-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold">Timesheets</h1>
            <p className="text-muted-foreground">
              Review and approve operator time entries • {getPayCycleDescription()}
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-industrial">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pending}</p>
                  <p className="text-xs text-muted-foreground">Pending Approval</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-industrial">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.approved}</p>
                  <p className="text-xs text-muted-foreground">Approved</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-industrial">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.rejected}</p>
                  <p className="text-xs text-muted-foreground">Rejected</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-industrial">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#ffd108]/20 rounded-lg">
                  <Clock className="h-5 w-5 text-[#ffd108]" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalHours.toFixed(1)}</p>
                  <p className="text-xs text-muted-foreground">Total Hours</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">All Pay Cycles</TabsTrigger>
            <TabsTrigger value="pending">
              Pending
              {stats.pending > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {stats.pending}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
          </TabsList>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by operator, job, or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={operatorFilter} onValueChange={setOperatorFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by operator" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Operators</SelectItem>
                {operators.map(op => (
                  <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Content - Pay Cycle Groups */}
          <TabsContent value={activeTab} className="mt-4">
            <Card className="card-industrial">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-pulse text-muted-foreground">Loading timesheets...</div>
                  </div>
                ) : filteredGroups.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <CalendarDays className="h-12 w-12 text-muted-foreground/50 mb-4" />
                    <h3 className="font-semibold text-lg">No Pay Cycles Found</h3>
                    <p className="text-muted-foreground text-sm">
                      No timesheet entries match your filters
                    </p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredGroups.map((group, idx) => (
                      <div 
                        key={`${group.period_start}_${group.period_end}`}
                        className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => openGroupDrawer(group)}
                        data-testid={`pay-cycle-group-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-3 bg-[#ffd108]/10 rounded-lg">
                              <CalendarDays className="h-6 w-6 text-[#ffd108]" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-lg">{formatPeriodLabel(group)}</h3>
                              <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                                <span className="flex items-center gap-1">
                                  <Users className="h-4 w-4" />
                                  {group.operator_count} operator{group.operator_count !== 1 ? 's' : ''}
                                </span>
                                <span>•</span>
                                <span>{group.total_entries} entries</span>
                                <span>•</span>
                                <span className="font-mono font-semibold text-foreground">
                                  {group.total_hours.toFixed(1)}h
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {group.pending_count > 0 && (
                              <Badge className="bg-orange-500 text-white">
                                {group.pending_count} pending
                              </Badge>
                            )}
                            {group.approved_count > 0 && (
                              <Badge className="bg-green-500 text-white">
                                {group.approved_count} approved
                              </Badge>
                            )}
                            {group.rejected_count > 0 && (
                              <Badge className="bg-red-500 text-white">
                                {group.rejected_count} rejected
                              </Badge>
                            )}
                            <ChevronRight className="h-5 w-5 text-muted-foreground" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Pay Cycle Group Drawer */}
        <Sheet open={groupDrawerOpen} onOpenChange={setGroupDrawerOpen}>
          <SheetContent side="right" className="w-full max-w-[1000px] sm:w-[1000px] overflow-y-auto">
            <SheetHeader className="pb-4 border-b">
              <SheetTitle className="font-heading text-xl flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-[#ffd108]" />
                {selectedGroup && formatPeriodLabel(selectedGroup)}
              </SheetTitle>
              {selectedGroup && (
                <p className="text-sm text-muted-foreground">
                  {selectedGroup.operator_count} operators • {selectedGroup.total_entries} entries • {selectedGroup.total_hours.toFixed(1)} total hours
                </p>
              )}
            </SheetHeader>
            
            {selectedGroup && (
              <div className="py-4">
                {/* Bulk actions */}
                {selectedTimesheets.length > 0 && (
                  <div className="mb-4 p-3 bg-muted rounded-lg flex items-center justify-between">
                    <span className="text-sm">{selectedTimesheets.length} timesheet(s) selected</span>
                    <Button size="sm" className="btn-volt" onClick={handleBulkApprove}>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Approve Selected
                    </Button>
                  </div>
                )}
                
                {/* Operator Groups */}
                <div className="space-y-2">
                  {getOperatorGroups(filteredGroupTimesheets).map((opGroup) => (
                    <div key={opGroup.operator_id} className="border rounded-lg overflow-hidden">
                      {/* Operator Header Row */}
                      <div 
                        className="p-4 bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors flex items-center justify-between"
                        onClick={() => setExpandedOperator(expandedOperator === opGroup.operator_id ? null : opGroup.operator_id)}
                        data-testid={`operator-group-${opGroup.operator_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-500/10 rounded-full">
                            <User className="h-5 w-5 text-blue-500" />
                          </div>
                          <div>
                            <h4 className="font-semibold">{opGroup.operator_name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {opGroup.timesheets.length} entries • <span className="font-mono font-medium text-foreground">{opGroup.total_hours.toFixed(1)}h</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {opGroup.pending_count > 0 && (
                            <Badge className="bg-orange-500 text-white text-xs">
                              {opGroup.pending_count} pending
                            </Badge>
                          )}
                          {opGroup.approved_count > 0 && (
                            <Badge className="bg-green-500 text-white text-xs">
                              {opGroup.approved_count} approved
                            </Badge>
                          )}
                          <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${expandedOperator === opGroup.operator_id ? 'rotate-180' : ''}`} />
                        </div>
                      </div>
                      
                      {/* Expanded Timesheet Entries */}
                      {expandedOperator === opGroup.operator_id && (
                        <div className="border-t">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-muted/20">
                                <TableHead className="w-[50px]">
                                  <Checkbox 
                                    checked={opGroup.timesheets.filter(t => t.status === 'submitted').every(t => selectedTimesheets.includes(t.id)) && opGroup.timesheets.some(t => t.status === 'submitted')}
                                    onCheckedChange={(checked) => {
                                      const pendingIds = opGroup.timesheets.filter(t => t.status === 'submitted').map(t => t.id);
                                      if (checked) {
                                        setSelectedTimesheets(prev => [...new Set([...prev, ...pendingIds])]);
                                      } else {
                                        setSelectedTimesheets(prev => prev.filter(id => !pendingIds.includes(id)));
                                      }
                                    }}
                                  />
                                </TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Job / Customer</TableHead>
                                <TableHead>Start</TableHead>
                                <TableHead>End</TableHead>
                                <TableHead>Hours</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {opGroup.timesheets.map((ts) => {
                                const StatusIcon = STATUS_CONFIG[ts.status]?.icon || Clock;
                                return (
                                  <TableRow key={ts.id}>
                                    <TableCell>
                                      {ts.status === 'submitted' && (
                                        <Checkbox 
                                          checked={selectedTimesheets.includes(ts.id)}
                                          onCheckedChange={() => toggleSelectTimesheet(ts.id)}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell className="font-mono text-sm">
                                      {formatDate(ts.date)}
                                    </TableCell>
                                    <TableCell>
                                      <div className="space-y-0.5">
                                        <p className="text-sm font-medium truncate max-w-[200px]">
                                          {ts.job_description || 'No description'}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                          {ts.customer_name} • {ts.site_name}
                                        </p>
                                      </div>
                                    </TableCell>
                                    <TableCell className="font-mono text-sm">
                                      {formatTime(ts.start_time)}
                                    </TableCell>
                                    <TableCell className="font-mono text-sm">
                                      {formatTime(ts.finish_time)}
                                    </TableCell>
                                    <TableCell>
                                      <span className="font-mono font-semibold">
                                        {ts.total_hours ? `${ts.total_hours.toFixed(1)}h` : '-'}
                                      </span>
                                    </TableCell>
                                    <TableCell>
                                      <Badge className={`${STATUS_CONFIG[ts.status]?.color} text-white`}>
                                        <StatusIcon className="h-3 w-3 mr-1" />
                                        {STATUS_CONFIG[ts.status]?.label}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex items-center justify-end gap-1">
                                        {ts.status === 'submitted' && (
                                          <>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                              onClick={(e) => { e.stopPropagation(); handleApprove(ts.id); }}
                                            >
                                              <Check className="h-4 w-4" />
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedTimesheet(ts);
                                                setRejectDialogOpen(true);
                                              }}
                                            >
                                              <XIcon className="h-4 w-4" />
                                            </Button>
                                          </>
                                        )}
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="h-8 w-8 p-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedTimesheet(ts);
                                            setViewDialogOpen(true);
                                          }}
                                        >
                                          <Eye className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <SheetFooter className="pt-4 border-t mt-6">
              <Button variant="outline" onClick={() => setGroupDrawerOpen(false)}>Close</Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* View Timesheet Details Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Timesheet Details</DialogTitle>
            </DialogHeader>
            {selectedTimesheet && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Operator</Label>
                    <p className="font-medium">{selectedTimesheet.operator_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Date</Label>
                    <p className="font-medium">{formatDate(selectedTimesheet.date)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Start Time</Label>
                    <p className="font-mono">{formatTime(selectedTimesheet.start_time)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">End Time</Label>
                    <p className="font-mono">{formatTime(selectedTimesheet.finish_time)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Break</Label>
                    <p className="font-mono">{selectedTimesheet.lunch_break_minutes || selectedTimesheet.break_minutes || 0} mins</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Total Hours</Label>
                    <p className="font-mono font-semibold text-lg">
                      {selectedTimesheet.total_hours ? `${selectedTimesheet.total_hours.toFixed(2)}h` : '-'}
                    </p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Job</Label>
                  <p className="font-medium">{selectedTimesheet.job_description || 'No description'}</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedTimesheet.customer_name} • {selectedTimesheet.site_name}
                  </p>
                </div>
                {selectedTimesheet.notes && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Notes</Label>
                    <p className="text-sm">{selectedTimesheet.notes}</p>
                  </div>
                )}
                {selectedTimesheet.status === 'rejected' && selectedTimesheet.rejection_reason && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <Label className="text-xs text-red-600">Rejection Reason</Label>
                    <p className="text-sm text-red-800">{selectedTimesheet.rejection_reason}</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <Badge className={`${STATUS_CONFIG[selectedTimesheet.status]?.color} text-white mt-1`}>
                    {STATUS_CONFIG[selectedTimesheet.status]?.label}
                  </Badge>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
              {selectedTimesheet?.status === 'submitted' && (
                <>
                  <Button 
                    variant="destructive"
                    onClick={() => {
                      setViewDialogOpen(false);
                      setRejectDialogOpen(true);
                    }}
                  >
                    Reject
                  </Button>
                  <Button 
                    className="btn-volt"
                    onClick={() => {
                      handleApprove(selectedTimesheet.id);
                      setViewDialogOpen(false);
                    }}
                  >
                    Approve
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Reject Timesheet</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Please provide a reason for rejecting this timesheet. The operator will be notified.
              </p>
              <div className="space-y-2">
                <Label>Rejection Reason</Label>
                <Textarea
                  placeholder="e.g., Hours do not match job requirements, incorrect break time..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setRejectDialogOpen(false);
                setRejectionReason('');
              }}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleReject}>
                Reject Timesheet
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </FeatureGate>
  );
};

export default Timesheets;
